import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { ShowStatusMessageEvent } from '@theme/events';

/**
 * A custom element that manages a Favorite button.
 */
class FavoriteComponent extends Component {
  /**
   * Handles the click event for the favorite button.
   * @param {boolean} loggedIn - If the customer is already logged in.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   * @returns {boolean} If a login redirect is required.
   */
  handleLogin(loggedIn, event) {
    event.preventDefault();

    if (!loggedIn) {
      window.location.href = Theme.routes.storefront_login_url;
      return true;
    }

    return false;
  }

  /**
   * Read the product information from the form and make the favorites
   * api call
   * @param {string} variantGid
   * @param {string} quantity
   */
  async addToFavorites(variantGid, quantity) {
    // Append the product to favorites
    const resp = await fetch('/apps/tbd', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        namespace: 'customer_settings',
        key: 'wishlist_item',
        appendTo: 'wishlist_items',
        value: {
          id: variantGid,
          qty: quantity,
        }
      })
    })
    if (!resp.ok) {
      throw new Error(`Failed adding to favorites: ${resp.statusText}`);
    }
  }

  /**
   * Read the quantity out o fa quantity selector
   * @param {Element} parent
   */
  quantityFromSelector(parent) {
    const quantitySelector = /** @type {any} */ (parent.querySelector('.quantity-selector'));
    return quantitySelector?.getValue() ?? '1';

  }

  variantGid = (/** @type {any} */ id) => `gid://shopify/ProductVariant/${id}`;
}

/**
 * A component for adding to favorites from a product page
 */
class AddToFavoritesComponent extends FavoriteComponent {
  #sessionKey = 'addToFavorites';

  connectedCallback() {
    // Use session storate to add favorites after a login redirect
    document.addEventListener('DOMContentLoaded', async (event) => {
      try {
        const stateValue = sessionStorage.getItem(this.#sessionKey);
        if (!stateValue) {
          return;
        }
        sessionStorage.removeItem(this.#sessionKey);

        const state = JSON.parse(stateValue);
        if (state.action == 'add') {
          await this.addToFavorites(state.variantGid, state.quantity);
          this.#sendStatusSuccess();
        }
      } catch (err) {
        console.error(err);
      }
    });
  }

  /**
   * Handles the click event for the favorite button.
   * @param {boolean} loggedIn - If the customer is already logged in.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  async handleClick(loggedIn, event) {
    try {
      const [variantGid, quantity] = this.#variantFromAddToCartForm();

      const requireRedirect = this.handleLogin(loggedIn, event);
      if (requireRedirect) {
        sessionStorage.removeItem(this.#sessionKey);
        sessionStorage.setItem(this.#sessionKey, JSON.stringify({action: 'add', variantGid, quantity}));
        return;
      }

      await this.addToFavorites(variantGid, quantity);
      this.#sendStatusSuccess();
    } catch(err) {
      console.error(err);
    }
  }

  /**
   * Reads the variant and quantity out of an add to cart form. Used on product pages that have variant pickers.
   */
  #variantFromAddToCartForm() {
    const form = this.closest('form');
    if (!form) throw new Error('Product form element missing');
    const formData = new FormData(form);
    const id = formData.get('id');
    if (!id) throw new Error('Id is required');
    const variantGid = this.variantGid(id);
    const quantity = this.quantityFromSelector(form);
    return [variantGid, quantity]
  }

  #sendStatusSuccess() {
    setTimeout(() => {
      window.dispatchEvent(new ShowStatusMessageEvent('add-to-favorites-success', 'success'));
    }, 0);
  }
}

if (!customElements.get('add-to-favorites-component')) {
  customElements.define('add-to-favorites-component', AddToFavoritesComponent);
}

/**
 * A component to move an item to favorites from a cart page or drawer
 */
class MoveToFavoritesComponent extends FavoriteComponent {
  #sessionKey = 'moveToFavorites';

  connectedCallback() {
    // Use session storate to add favorites after a login redirect
    document.addEventListener('DOMContentLoaded', async (event) => {
      try {
        const stateValue = sessionStorage.getItem(this.#sessionKey);
        if (!stateValue) {
          return;
        }
        sessionStorage.removeItem(this.#sessionKey);

        const state = JSON.parse(stateValue);
        if (state.action == 'move') {
          await this.addToFavorites(state.variantGid, state.quantity);
          this.#removeFromCart(state.index);
          this.#sendStatusSuccess();
        }
      } catch (err) {
        console.error(err);
      }
    });
  }

  /**
   * Handles the click event for the favorite button.
   * @param {boolean} loggedIn - If the customer is already logged in.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  async handleClick(loggedIn, event) {
    try {
      const [variantGid, quantity, index] = this.#variantFromDataset();

      const requireRedirect = this.handleLogin(loggedIn, event);
      if (requireRedirect) {
        sessionStorage.removeItem(this.#sessionKey);
        sessionStorage.setItem(this.#sessionKey, JSON.stringify({action: 'move', variantGid, quantity, index}));
        return;
      }

      await this.addToFavorites(variantGid, quantity);
      this.#removeFromCart(index);
      this.#sendStatusSuccess();
    } catch(err) {
      console.error(err);
    }
  }

  /**
   * Reads variant data out of the dataset
   */
  #variantFromDataset() {
    const id = this.dataset.variantId;
    if (!id) throw new Error('Id is required');
    const variantGid = this.variantGid(id);

    const parentSelector = this.dataset.parentSelector;
    if (!parentSelector) throw new Error('Parent selector is required');
    const parent = this.closest(parentSelector);
    if (!parent) throw new Error('Parent is required');
    const quantity = this.quantityFromSelector(parent);
    const index = this.dataset.index;
    if (!index) throw new Error('Index is required');

    return [variantGid, quantity, index]
  }

  /**
   * Removes the line item index from the cart
   * @param {string} index
   */
  #removeFromCart(index) {
    const cartItems = this.closest('cart-items-component');
    if (!(cartItems instanceof CartItemsComponent)) throw new Error('CartItems is required');
    cartItems.onLineItemRemove(parseInt(index));
  }

  #sendStatusSuccess() {
    setTimeout(() => {
      window.dispatchEvent(new ShowStatusMessageEvent('move-to-favorites-success', 'success'));
    }, 0);
  }
}

if (!customElements.get('move-to-favorites-component')) {
  customElements.define('move-to-favorites-component', MoveToFavoritesComponent);
}
